// utils/apiHelpers.js
import { fromException, fromHttpResponse, fromApiResponse } from '@/utils/apiError';
import { notifySessionEnded } from '@/utils/sessionExpiry';

/**
 * Recursively normalizes object keys to camelCase.
 * Handles nested objects and arrays.
 *
 * @param {any} obj - The object/array/value to normalize
 * @returns {any} - The normalized object with camelCase keys
 *
 * @example
 * normalizeKeys({ OrgId: '123', IsAdmin: true })
 * // Returns: { orgId: '123', isAdmin: true }
 *
 * @example
 * normalizeKeys({ Organizations: [{ OrgName: 'Test' }] })
 * // Returns: { organizations: [{ orgName: 'Test' }] }
 */
/**
 * `normalizeKeys` for a top-level API response, plus the one thing every call
 * site kept forgetting: telling the app when the server says the session is over.
 *
 * WHY THIS EXISTS
 * One session per user is deliberate (see OUTSTANDING_ITEMS §A), so signing in
 * anywhere evicts the previous session. The evicted tab is not notified — its
 * next request comes back HTTP 200 with `success: false, errorCode: 7`. Around
 * 120 call sites are shaped like:
 *
 *     if (data.success) setMembers(data.members || []);   // no else
 *
 * so the rejection is swallowed and the page renders an empty list. Being signed
 * out on your phone therefore looks exactly like your data having vanished —
 * which fooled us during testing on 2026-08-11, so it will certainly fool a
 * beta tester.
 *
 * WHY NOT INSIDE normalizeKeys
 * `normalizeKeys` recurses into every nested object and array. A `success` key
 * on some nested payload would fire this repeatedly and wrongly. The check
 * belongs to the RESPONSE, so it lives in a separate top-level wrapper.
 *
 * Reporting is latched in sessionExpiry.js, so a page firing six requests that
 * all fail together still produces exactly one sign-out.
 *
 * @param {any} json parsed JSON body of an API response
 * @returns {any} the same value normalizeKeys would return
 */
export function readApiJson(json) {
    const data = normalizeKeys(json);
    if (data && typeof data === 'object' && !Array.isArray(data) && data.success === false) {
        const error = fromApiResponse(data, {});
        if (error.sessionEnded) notifySessionEnded(error);
    }
    return data;
}

export function normalizeKeys(obj) {
    // Handle null/undefined
    if (obj === null || obj === undefined) {
        return obj;
    }

    // Handle arrays - normalize each element
    if (Array.isArray(obj)) {
        return obj.map(item => normalizeKeys(item));
    }

    // Handle Date objects - return as-is
    if (obj instanceof Date) {
        return obj;
    }

    // Handle plain objects
    if (typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            // Convert first character to lowercase (PascalCase -> camelCase)
            const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
            // GeoBounds is an opaque wire object (server-computed GeoPDF bounds:
            // Format/GPTS/West/East/North/South + ExtrapolatedPageCorners whose
            // Item1/Item2 come from a C# ValueTuple). It must be echoed back to
            // the server EXACTLY as received, so rename the key but do NOT recurse
            // into the value — camelCasing its internals would break the contract.
            if (camelKey === 'geoBounds') {
                acc.geoBounds = obj[key];
            } else {
                // Recursively normalize the value
                acc[camelKey] = normalizeKeys(obj[key]);
            }
            return acc;
        }, {});
    }

    // Return primitives as-is (string, number, boolean)
    return obj;
}

/**
 * Wrapper for fetch that automatically normalizes response keys to camelCase.
 *
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<any>} - The normalized JSON response
 *
 * @example
 * const data = await fetchWithNormalize('/api/orgs', { method: 'POST', body: ... });
 * // Response keys are automatically converted to camelCase
 */
export async function fetchWithNormalize(url, options = {}) {
    const response = await fetch(url, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    const data = await response.json();
    return normalizeKeys(data);
}

/**
 * Helper to check if a response was successful.
 * Handles both 'Success' and 'success' (though after normalization it will always be 'success').
 *
 * @param {object} response - The API response object
 * @returns {boolean} - Whether the response indicates success
 */
export function isSuccessResponse(response) {
    return response?.success === true;
}

/**
 * Helper to extract error message from API response.
 *
 * @param {object} response - The API response object
 * @param {string} fallback - Fallback message if no error found
 * @returns {string} - The error message
 */
export function getErrorMessage(response, fallback = 'An error occurred') {
    return response?.errorMessage || response?.error || fallback;
}

/**
 * One call that does the whole request/response dance correctly.
 *
 * The portal makes **481 raw `fetch` calls**, each repeating the same four
 * steps — and each getting the error handling slightly differently. This
 * collapses them into one place:
 *
 *   1. transport failure (offline, DNS, TLS)  → normalised, never a raw TypeError
 *   2. non-OK HTTP status                     → normalised, never an HTML page
 *   3. unparseable body                       → normalised, never a JSON crash
 *   4. `success: false` in the body           → normalised, with the code
 *
 * **Never throws.** Callers branch on `ok` instead of wrapping every call in a
 * try/catch that has to guess what it caught:
 *
 *     const { ok, data, error } = await apiPost(url, body, { action: 'loadApplet' });
 *     if (!ok) return handleError(error);
 *
 * @param {string} url
 * @param {object} body            request body, sent as JSON
 * @param {object} [context]       { action } — carried into the error log
 * @returns {Promise<{ok: boolean, data?: object, error?: object}>}
 */
export async function apiPost(url, body, context = {}) {
    const ctx = { endpoint: url, method: 'POST', ...context };

    let response;
    try {
        response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    } catch (err) {
        // The request never left / never arrived. This is the case that showed a
        // client "TypeError: Load failed".
        return { ok: false, error: fromException(err, ctx) };
    }

    if (!response.ok) {
        const error = await fromHttpResponse(response, ctx);
        // Reported here rather than left to the caller: helpers that swallow a
        // failure to keep the page rendering would otherwise swallow this too.
        if (error.sessionEnded) notifySessionEnded(error);
        return { ok: false, error };
    }

    let data;
    try {
        // Deliberately normalizeKeys, NOT readApiJson: apiPost reports an ended
        // session itself a few lines below, with its own error context. Routing
        // through readApiJson would report the same thing twice — harmless
        // because sessionExpiry latches, but misleading to read.
        data = normalizeKeys(await response.json());
    } catch (err) {
        // 200 with a body that is not JSON — a proxy interstitial, or a truncated
        // response on a flaky connection.
        return { ok: false, error: fromException(err, ctx) };
    }

    if (data && data.success === false) {
        const error = fromApiResponse(data, ctx);
        // The common case: HTTP 200 with `errorCode: 7` in the body. Every portal
        // endpoint answers an expired key this way, so this is the line that
        // actually catches a stale session.
        if (error.sessionEnded) notifySessionEnded(error);
        return { ok: false, error, data };
    }

    return { ok: true, data };
}

/**
 * `apiPost` for a multipart upload: same four failure modes, same `{ok, data,
 * error}` shape, but the body is a FormData and the auth travels in HEADERS
 * (`Key`, `OrgId`, …) because a multipart request has no JSON body to carry it.
 *
 * Deliberately sets NO Content-Type: the browser writes the multipart boundary
 * itself, and a hand-set value breaks the upload silently (the server reads an
 * empty form). Added 2026-09-14 for the AI applet endpoints, whose refusals
 * come back HTTP 200 with `success:false` and a `rejectionCode` — so `data` is
 * returned alongside `error` on that branch, exactly as `apiPost` does.
 *
 * @param {string}   url
 * @param {FormData} form
 * @param {object}   [options]  { headers, ...context } — headers sent verbatim;
 *                              the rest (e.g. action) is carried into the error log
 */
export async function apiPostForm(url, form, options = {}) {
    const { headers = {}, ...context } = options;
    const ctx = { endpoint: url, method: 'POST', ...context };

    let response;
    try {
        response = await fetch(url, { method: 'POST', headers, body: form });
    } catch (err) {
        return { ok: false, error: fromException(err, ctx) };
    }

    if (!response.ok) {
        const error = await fromHttpResponse(response, ctx);
        if (error.sessionEnded) notifySessionEnded(error);
        return { ok: false, error };
    }

    let data;
    try {
        data = normalizeKeys(await response.json());
    } catch (err) {
        return { ok: false, error: fromException(err, ctx) };
    }

    if (data && data.success === false) {
        const error = fromApiResponse(data, ctx);
        if (error.sessionEnded) notifySessionEnded(error);
        return { ok: false, error, data };
    }

    return { ok: true, data };
}

/**
 * A readable message for a failed upload/download response.
 *
 * The upload endpoints return a PLAIN-TEXT body on non-2xx — but only when the
 * request actually reached the app. A gateway failure (502/503/504 from the
 * reverse proxy, e.g. a large upload exceeding the proxy's limit) returns a
 * full HTML error page instead, and dumping that into a toast renders an
 * entire `<!DOCTYPE html>` document on screen (seen live 2026-08-03 uploading
 * a 56 MB GeoPDF). Anything that looks like markup, or is implausibly long for
 * a message, is replaced with a clean status-based line.
 */
export async function uploadErrorMessage(res, fallback = 'Upload failed') {
    let body = '';
    try { body = (await res.text()) || ''; } catch { /* body already consumed */ }
    const text = body.trim();
    const looksLikeHtml = /^<!doctype|^<html|<\/html>/i.test(text);
    if (!text || looksLikeHtml || text.length > 300) {
        if (res.status >= 502 && res.status <= 504) {
            return `${fallback} — the server did not respond (${res.status}). The file may be too large.`;
        }
        return `${fallback} (${res.status})`;
    }
    return text;
}

/**
 * The same job as `uploadErrorMessage`, but routed through the shared wording so
 * uploads say what the rest of the portal says.
 *
 * Kept separate rather than changing `uploadErrorMessage` in place: that function
 * has 29 call sites whose current behaviour — passing the server's own plain-text
 * validation line straight through, e.g. "File size cannot exceed 140 MB" — is
 * genuinely useful and specific to uploads. Changing it silently would trade a
 * good message for a generic one. New call sites should prefer this; existing
 * ones can migrate when someone is already in the file.
 *
 * @returns {Promise<object>} a normalised error, ready for `handleError`
 */
export async function uploadError(res, context = {}) {
    return fromHttpResponse(res, { method: 'POST', ...context });
}
